@use "@/styles/variables" as *;

$sidebar-width-collapsed: 64px;
$sidebar-width-expanded: 240px;
$sidebar-top-offset: 8.5vh;
$sidebar-transition: 0.28s cubic-bezier(0.4, 0, 0.2, 1);
$nav-item-radius: $border-radius;
$nav-text: rgba(255, 255, 255, 0.88);
$nav-text-muted: rgba(255, 255, 255, 0.78);
$nav-hover-overlay: rgba(0, 0, 0, 0.12);
$nav-active-overlay: rgba(0, 0, 0, 0.22);

@mixin sidebar-scrollbar($thumb-color, $thumb-hover) {
	&::-webkit-scrollbar {
		width: 4px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: $thumb-color;
		border-radius: $border-radius-sm;

		&:hover {
			background: $thumb-hover;
		}
	}
}

@mixin nav-interactive-base {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	background: transparent;
	border: none;
	cursor: pointer;
	text-align: left;
	text-decoration: none;
	box-sizing: border-box;
	transition:
		background-color 0.2s ease,
		color 0.2s ease;

	&:focus-visible {
		outline: 2px solid rgba(255, 255, 255, 0.85);
		outline-offset: 2px;
	}
}

@mixin nav-item-states($text-color: $nav-text) {
	color: $text-color;

	svg {
		flex-shrink: 0;
		stroke: $text-color;
		transition: stroke 0.2s ease;
	}

	&:hover {
		background-color: $nav-hover-overlay;
		color: #fff;

		svg {
			stroke: #fff;
		}
	}

	&.active {
		background-color: $nav-active-overlay;
		color: #fff;

		svg {
			stroke: #fff;
		}

		&:hover {
			background-color: rgba(0, 0, 0, 0.28);
		}
	}
}

@mixin collapsed-tooltip {
	.collapsed & {
		justify-content: center;
		padding: 0.75rem;
		white-space: nowrap;

		&::after {
			content: attr(title);
			position: absolute;
			left: calc(100% + 0.65rem);
			padding: 0.45rem 0.7rem;
			background-color: $gray-700;
			color: #fff;
			border-radius: $border-radius;
			font-size: 0.8125rem;
			font-weight: 500;
			white-space: nowrap;
			opacity: 0;
			pointer-events: none;
			transform: translateX(-4px);
			transition:
				opacity 0.18s ease,
				transform 0.18s ease;
			z-index: 1000;
			box-shadow: 0 8px 20px rgba(15, 23, 42, 0.28);
		}

		&:hover::after {
			opacity: 1;
			transform: translateX(0);
		}
	}
}

.sidebar {
	position: fixed;
	left: 0;
	top: $sidebar-top-offset;
	height: calc(100vh - #{$sidebar-top-offset});
	background: linear-gradient(180deg, $primary-color 0%, $primary-hover 100%);
	display: flex;
	flex-direction: column;
	transition: width $sidebar-transition;
	z-index: 99;
	border-right: 1px solid $secondary-color;
	box-shadow: 2px 0 12px rgba($secondary-color, 0.18);

	&.collapsed {
		width: $sidebar-width-collapsed;
		overflow: visible;
	}

	&.expanded {
		width: $sidebar-width-expanded;
		overflow: hidden;
	}
}

.sidebarContent {
	display: flex;
	flex-direction: column;
	padding: 0.85rem 0;
	gap: 0.2rem;
	flex: 1;
	height: 100%;
	@include sidebar-scrollbar($secondary-color, $primary-hover);
}

.expanded .sidebarContent {
	overflow-y: auto;
	overflow-x: hidden;
}

.collapsed .sidebarContent {
	overflow: visible;
}

.menuGroup {
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
}

.expanded .menuGroup {
	position: static;
}

.collapsed .menuGroup {
	position: relative;
	overflow: visible;
}

.menuItem {
	@include nav-interactive-base;
	@include nav-item-states;
	@include collapsed-tooltip;
	padding: 0.7rem 0.95rem;
	margin: 0 0.45rem;
	border-radius: $nav-item-radius;
	font-size: 0.875rem;
	font-weight: 500;
	white-space: normal;
	word-wrap: break-word;
	position: relative;
	width: calc(100% - 0.9rem);
	min-height: 44px;
}

.menuLabel {
	flex: 1;
	line-height: 1.35;
	word-break: break-word;
	letter-spacing: 0.01em;
}

.icon {
	flex-shrink: 0;
}

.submenuContainer {
	display: none;
	flex-direction: column;
	gap: 0.1rem;
	padding-left: 2.35rem;
	margin-top: 0.15rem;

	&.submenuExpanded {
		display: flex;
	}
}

.collapsed .submenuContainer {
	display: none;
	position: fixed;
	left: $sidebar-width-collapsed;
	padding: 0.45rem;
	background-color: $gray-700;
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: $nav-item-radius;
	box-shadow:
		0 16px 28px rgba(15, 23, 42, 0.32),
		0 6px 12px rgba(15, 23, 42, 0.18);
	min-width: 210px;
	max-width: 280px;
	max-height: calc(100vh - 100px);
	overflow-y: auto;
	overflow-x: hidden;
	z-index: 10000;
	@include sidebar-scrollbar($gray-500, $gray-400);
}

.collapsed .submenuContainer.submenuPinned {
	display: flex !important;
}

.submenuItem {
	@include nav-interactive-base;
	@include nav-item-states($nav-text-muted);
	padding: 0.5rem 0.85rem;
	border-radius: $border-radius-sm;
	font-size: 0.875rem;
	font-weight: 400;
	white-space: normal;
	word-wrap: break-word;
	width: 100%;
	line-height: 1.35;

	span {
		flex: 1;
		word-break: break-word;
	}

	.collapsed & {
		justify-content: flex-start;
		padding: 0.5rem 0.75rem;
		color: $nav-text;

		svg {
			stroke: $nav-text;
		}
	}
}

.mobileBackdrop {
	display: none;
	position: fixed;
	inset: 0;
	background: rgba($gray-700, 0.45);
	backdrop-filter: blur(2px);
	border: 0;
	padding: 0;
	z-index: 98;
	cursor: pointer;
}

@media (max-width: 768px) {
	.sidebar {
		top: 0;
		height: 100vh;
		z-index: 100;

		&.collapsed {
			width: 0;
			border-right: none;
			overflow: hidden;
			pointer-events: none;
			box-shadow: none;
		}

		&.collapsed .sidebarContent,
		&.collapsed .menuGroup,
		&.collapsed .menuItem,
		&.collapsed .submenuContainer {
			display: none !important;
		}

		&.expanded {
			width: min(100%, 300px);
			box-shadow: 0 14px 36px rgba(15, 23, 42, 0.28);
		}

		&.expanded .sidebarContent {
			padding-top: 1.15rem;
		}

		&.expanded .menuItem {
			font-size: 1rem;
			padding: 0.9rem 1rem;
			min-height: 52px;
			gap: 0.9rem;

			svg {
				width: 24px;
				height: 24px;
			}
		}

		&.expanded .submenuItem {
			font-size: 0.9375rem;
			padding: 0.65rem 0.95rem;
			min-height: 44px;

			svg {
				width: 20px;
				height: 20px;
			}
		}
	}

	.mobileBackdrop {
		display: block;
		z-index: 99;
	}
}
